import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTickets } from './TicketContext';
import TicketService from '../../../services/ticketing/TicketingServices';
import Select from 'react-select';
import { getTrans as t } from '../../../Localization/translations';
import Tooltip from "@tippyjs/react";
import Helper from "../../../Helper";

const getStatusClass = (status) => {
  switch (status) {
    case "new": return "badge bg-info text-dark";
    case "open": return "badge bg-success";
    case "closed": return "badge bg-danger";
    case "resolved": return "badge bg-success";
    case "replied": return "badge bg-warning text-dark";
    default: return "badge bg-secondary";
  }
};

export default function ViewUserTicket() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get("ticket_id");
  const { tickets, actions } = useTickets();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
  });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const permissions = localStorage.getItem('permissions');
  const hasViewAllTicketsPermission = permissions && permissions.split(',').includes('view_all_tickets');

  const [currentUser] = useState({ email: "user@gmail.com", company: "Athena" });
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  // Function to refresh comments
  const refreshComments = useCallback(async () => {
    if (!ticket?.ticket_id) return;

    try {
      console.log('User - Refreshing comments for ticket:', ticket.ticket_id);
      const commentsResponse = await TicketService.getTicketComments(ticket.ticket_id);

      let commentsData = [];
      if (commentsResponse?.data?.data && Array.isArray(commentsResponse.data.data)) {
        commentsData = commentsResponse.data.data;
      } else if (commentsResponse?.data && Array.isArray(commentsResponse.data)) {
        commentsData = commentsResponse.data;
      } else if (Array.isArray(commentsResponse)) {
        commentsData = commentsResponse;
      }

      console.log('User - Auto-refreshed comments:', commentsData.length);
      setComments(commentsData);
    } catch (err) {
      console.error('User - Error refreshing comments:', err);
    }
  }, [ticket?.ticket_id]);

  // PERMISSION CHECK: Only customer users can access this page
  const customerPermission = localStorage.getItem('customer');
  useEffect(() => {
    if (customerPermission !== 'Customer') {
      navigate('/');
      return;
    }
  }, [customerPermission, navigate]);

  const loadTicketData = useCallback(async () => {
    if (hasLoadedOnce) return;

    try {
      setLoading(true);
      setError(null);
      setHasLoadedOnce(true);

      const ticketData = await actions.getTicketById(ticketId);
      if (ticketData) {
        setTicket(ticketData);
        setEditFormData({
          title: ticketData.title || '',
          description: ticketData.description || '',
          priority: ticketData.priority || 'medium',
        });

        const commentsResponse = await TicketService.getTicketComments(ticketData.ticket_id);
        const commentsData = commentsResponse?.data?.data || commentsResponse?.data || [];
        setComments(commentsData);

        setAttachments(ticketData.attachments || []);
      } else {
        setError('Ticket not found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId, actions, hasLoadedOnce]);

  useEffect(() => {
    if (ticketId && !hasLoadedOnce) {
      loadTicketData();
    }
  }, []);

  useEffect(() => {
    setHasLoadedOnce(false);
    setTicket(null);
    setLoading(true);
    setError(null);
  }, []);

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Updating ticket with data:', editFormData);
      console.log('Ticket ID:', ticket.ticket_id);

      const response = await TicketService.updateTicket(ticket.ticket_id, editFormData);
      console.log('Update response:', response);

      if (response && (response.data || response.status === 200 || response.status === 201)) {
        setTicket(prev => ({
          ...prev,
          ...editFormData
        }));

        setIsEditing(false);

        toast.success("Ticket updated successfully", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });

        setTimeout(async () => {
          try {
            const updatedTicket = await actions.getTicketById(ticket.ticket_id);
            if (updatedTicket) {
              setTicket(updatedTicket);
              setEditFormData({
                title: updatedTicket.title || '',
                description: updatedTicket.description || '',
                priority: updatedTicket.priority || 'medium',
              });
            }
          } catch (refreshError) {
            console.error('Error refreshing ticket data:', refreshError);
          }
        }, 1500);
      } else {
        throw new Error('Update request did not return expected response');
      }
    } catch (err) {
      console.error('Error updating ticket:', err);

      let errorMessage = 'Unknown error occurred';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError('Failed to update ticket: ' + errorMessage);

      toast.error('Failed to update ticket: ' + errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !ticket) return;

    try {
      const commentData = {
        content: newComment,
        is_internal: false
      };
      console.log('User - Sending comment data:', commentData);

      const addCommentResponse = await TicketService.addTicketComment(ticket.ticket_id, commentData);
      console.log('User - Add comment response:', addCommentResponse);

      await new Promise(resolve => setTimeout(resolve, 1000));

      const commentsResponse = await TicketService.getTicketComments(ticket.ticket_id);
      console.log('User - Refreshed comments response:', commentsResponse);

      let commentsData = [];
      if (commentsResponse?.data?.data && Array.isArray(commentsResponse.data.data)) {
        commentsData = commentsResponse.data.data;
      } else if (commentsResponse?.data && Array.isArray(commentsResponse.data)) {
        commentsData = commentsResponse.data;
      } else if (Array.isArray(commentsResponse)) {
        commentsData = commentsResponse;
      }

      console.log('User - Extracted refreshed comments data:', commentsData);
      console.log('User - Comments count:', commentsData.length);

      setComments(commentsData);
      setNewComment('');
      setError(null);

      toast.success("Comment added successfully", {
        position: "top-right",
        autoClose: 1000,
      });
    } catch (err) {
      console.error('User - Error adding comment:', err);
      setError('Failed to add comment: ' + err.message);
      toast.error('Failed to add comment: ' + err.message, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleUploadAttachment = async () => {
    if (!selectedFile || !ticket) return;

    try {
      const uploadResponse = await TicketService.uploadAttachment(ticket.ticket_id, selectedFile);
      const uploadedAttachment = uploadResponse?.data?.data || uploadResponse?.data;

      setAttachments(prev => Array.isArray(prev) ? [...prev, uploadedAttachment] : [uploadedAttachment]);

      setSelectedFile(null);
      toast.success("Attachment uploaded successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (err) {
      console.error('Error uploading attachment:', err);
      toast.error('Failed to upload attachment: ' + (err.response?.data?.message || err.message), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleLogout = async () => {
    try {
      await TicketService.logout();
    } catch (err) {
      //console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error!</h4>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => navigate('/customerticket')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">Ticket Not Found</h4>
            <p>The requested ticket could not be found.</p>
            <button className="btn btn-primary" onClick={() => navigate('/customerticket')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid bg-light min-vh-100 p-4">
      <main>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4>Ticket {ticket.ticket_id}</h4>
            <span className={`badge ${getStatusClass(ticket.status)}`}>
              {ticket.status_display || ticket.status}
            </span>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary mb-0 btn-sm" onClick={() => navigate('/customerticket')}>
              Back
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-8 col-md-12">
            <div className="card shadow-sm h-100">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{t("Ticket_info")}</h5>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    disabled={loading}
                    title={isEditing ? "Cancel Edit" : "Edit Ticket"}
                    style={{
                      border: "none", background: "white", padding: "4px", borderRadius: "6px", cursor: "pointer"
                    }}
                  >
                    <img src="/static/images/ic-edit.svg" alt="Edit ticket" />
                  </button>


                </div>

              </div>
              <div className="card-body">
                {isEditing ? (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <span className="sub-title tc">
                          {t("Subject")}{" "}
                          <Tooltip content={t("Subject_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control mb-3"
                          value={editFormData.title}
                          onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                        />
                        <span className="sub-title tc">
                          {t("priority")}{" "}
                          <Tooltip content={t("priority_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <select
                          className="form-control form-select mb-3"
                          value={editFormData.priority}
                          onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <span className="sub-title tc">
                          {t("Description")}{" "}
                          <Tooltip content={t("description_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <textarea
                          className="form-control textareaticket"
                          style={{ height: "115px" }}
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("category")}{" "}
                          <Tooltip content={t("category_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={ticket.category_name}
                          disabled
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("subcategory")}{" "}
                          <Tooltip content={t("subcategory_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={ticket.sub_category_name}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("company")}{" "}
                          <Tooltip content={t("company_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={ticket.company_name}
                          disabled
                        />
                      </div>
                      {hasViewAllTicketsPermission && (
                        <div className="col-md-6 mb-3">
                          <span className="sub-title tc">
                            {t("assigned_to")}{" "}
                            <Tooltip content={t("assigned_to_Tooltip")}>
                              <i className="fa fa-light fa-circle-info"></i>
                            </Tooltip>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={ticket.assigned_to_name || 'Unassigned'}
                            disabled
                          />
                        </div>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("created_by")}{" "}
                          <Tooltip content={t("created_by_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={ticket.client_name}
                          disabled
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("created")}{" "}
                          <Tooltip content={t("created_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={ticket.created_at ? Helper.DateNormal(ticket.created_at) : ""}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("due_date")}{" "}
                          <Tooltip content={t("due_date_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={ticket.due_date ? Helper.DateNormal(ticket.due_date) : ''}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <button
                        className="btn btn-secondary me-2"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveEdit}
                      >
                        Update
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="row">
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("Subject")}{" "}
                          <Tooltip content={t("Subject_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">{ticket.title}</span>
                        </div>
                      </div>
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("Description")}{" "}
                          <Tooltip content={t("description_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">{ticket.description || 'No description provided'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("priority")}{" "}
                          <Tooltip content={t("priority_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">{ticket.priority_display || ticket.priority}</span>
                        </div>
                      </div>
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("category")}{" "}
                          <Tooltip content={t("category_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">{ticket.category_name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("subcategory")}{" "}
                          <Tooltip content={t("subcategory_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">{ticket.sub_category_name}</span>
                        </div>
                      </div>
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("company")}{" "}
                          <Tooltip content={t("company_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">{ticket.company_name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      {hasViewAllTicketsPermission && (
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <label className="col-form-label">
                            {t("assigned_to")}{" "}
                            <Tooltip content={t("assigned_to_Tooltip")}>
                              <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                            </Tooltip>
                          </label>
                          <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                            <span className="view-label p">{ticket.assigned_to_name || 'Unassigned'}</span>
                          </div>
                        </div>
                      )}
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("created_by")}{" "}
                          <Tooltip content={t("created_by_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">{ticket.client_name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("created")}{" "}
                          <Tooltip content={t("created_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">
                            {ticket.created_at ? Helper.DateNormal(ticket.created_at) : "None"}
                          </span>
                        </div>
                      </div>
                      <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                        <label className="col-form-label">
                          {t("due_date")}{" "}
                          <Tooltip content={t("due_date_Tooltip")}>
                            <i className="fa fa-light fa-circle-info" aria-hidden="true"></i>
                          </Tooltip>
                        </label>
                        <div className="col-xl-6 d-flex" style={{ justifyContent: "space-between" }}>
                          <span className="view-label p">
                            {ticket.due_date ? Helper.DateNormal(ticket.due_date) : "None"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <hr className="my-4" />
                    <div className="col-12">
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="mb-0">
                            {t("comments")} ({comments.length})
                          </h5>
                        </div>
                        <div className="mb-3">
                          <textarea
                            className="form-control"
                            rows="3"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder={t("Type your comment here...")}
                          ></textarea>
                          <div className="d-flex justify-content-end">
                            <button
                              className="btn btn-primary btn-sm mt-2"
                              onClick={handleAddComment}
                              disabled={!newComment.trim()}
                            >
                              Add Comment
                            </button>
                          </div>
                        </div>
                        {comments.length === 0 ? (
                          <p className="text-muted">No comments yet.</p>
                        ) : (
                          <div
                            className="comments-list"
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              padding: "0.5rem",
                              backgroundColor: "var(--bs-table-bg)",
                              borderBottomWidth: "1px",
                              boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                              position: "relative",
                            }}
                            aria-label="Comments list with scrollbar"
                          >
                            {[...comments].reverse().map((comment, index) => (
                              <div key={comment.id || index} className="border-bottom pb-2 mb-2">
                                <div className="d-flex justify-content-between">
                                  <small>
                                    {comment.client_name || (comment.is_internal ? 'Company' : 'Customer')}
                                  </small>
                                  <small className="text-muted">
                                    {new Date(comment.created_at).toLocaleString()}
                                  </small>
                                </div>
                                <p className="mb-0">{comment.content || comment.comment || comment.message || 'No content'}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-12">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="mb-4">
                  <label className="col-form-label">
                    {t("Upload Attachment")}{" "}
                    <Tooltip content={t("upload_attachment_Tooltip")}>
                      <i className="fa fa-light fa-circle-info"></i>
                    </Tooltip>
                  </label>
                  <input
                    type="file"
                    className="form-control mb-2"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                  <div className="d-flex justify-content-end">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleUploadAttachment}
                      disabled={!selectedFile}
                    >
                      UPLOAD
                    </button>
                  </div>
                </div>
                <div className="col-12">
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">
                        Documents ({attachments.length})
                      </h5>
                    </div>
                    <div className="documents-list p-2" style={{ maxHeight: "500px", overflowX: "hidden", overflowY: "auto" }}>
                      {attachments.length > 0 ? (
                        <>
                          {attachments.map((doc) => {
                            const fileName = doc.original_filename || doc.filename || '';
                            const extension = fileName.split('.').pop()?.toLowerCase() || '';
                            return (
                              <div
                                className="document-item d-flex align-items-center mb-2"
                                key={doc.id}
                                style={{ flexWrap: "nowrap" }}
                              >
                                <div className="d-flex align-items-center flex-grow-1 me-2">
                                  <div className="me-2 fs-5">
                                    {extension === "pdf"
                                      ? "📄"
                                      : ['doc', 'docx'].includes(extension)
                                        ? "📁"
                                        : ['jpg', 'jpeg', 'png'].includes(extension)
                                          ? "🖼️"
                                          : "🖼️"}
                                  </div>
                                  <div className="text-truncate" style={{ maxWidth: "200px" }}>
                                    <div className="fw-semibold small">{fileName}</div>
                                    <div className="text-muted small">
                                      {new Date(doc.created_at || doc.uploaded_at).toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  className="btn btn-primary btn-sm"
                                  style={{ minWidth: "80px", whiteSpace: "nowrap" }}
                                  onClick={() => {
                                    if (doc.file || doc.url) {
                                      window.open(doc.file || doc.url, '_blank', 'noopener,noreferrer');
                                    } else {
                                      toast.error('No download link available', {
                                        position: "top-right",
                                        autoClose: 3000,
                                      });
                                    }
                                  }}
                                >
                                  Download
                                </button>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted">No documents available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ToastContainer />
    </div>
  );
}